import { useCallback, useEffect, useMemo, useState } from "react";
import { Timer, CalendarDays, Eye, Download } from "lucide-react";
import AdminLayout from "../../layouts/AdminLayout";
import { apiRequest, PROXY_URL } from "../../utils/apiRequest";
import FullPageSpinner from "../../components/FullPageSpinner";

const formatHoursWorked = (totalMinutes) => {
  const safeMinutes = Math.max(0, Number(totalMinutes) || 0);
  const hours = Math.floor(safeMinutes / 60);
  const minutes = safeMinutes % 60;

  return `${hours}h ${String(minutes).padStart(2, "0")}m`;
};

const formatDisplayDate = (dateString) => {
  if (!dateString) return "—";

  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return "—";

  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(date);
};

const formatDisplayTime = (dateString) => {
  if (!dateString) return "—";

  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return "—";

  return new Intl.DateTimeFormat("en-GB", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).format(date);
};

const formatDateInputValue = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getCurrentWeekRange = () => {
  const today = new Date();
  const currentDay = today.getDay();
  const mondayOffset = currentDay === 0 ? -6 : 1 - currentDay;

  const weekStartDate = new Date(today);
  weekStartDate.setHours(0, 0, 0, 0);
  weekStartDate.setDate(today.getDate() + mondayOffset);

  const weekEndDate = new Date(weekStartDate);
  weekEndDate.setDate(weekStartDate.getDate() + 6);

  return {
    weekStart: formatDateInputValue(weekStartDate),
    weekEnd: formatDateInputValue(weekEndDate),
  };
};

function TimesheetViewModal({ isOpen, onClose, loading, error, timesheet }) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 px-4">
      <div className="flex max-h-[90vh] w-full max-w-3xl flex-col rounded-2xl bg-white shadow-xl">
        <div className="flex shrink-0 items-center justify-between border-b border-slate-200 px-6 py-5">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">
              Timesheet
            </h3>
            <p className="mt-1 text-sm text-slate-500">
              {timesheet
                ? `${timesheet.staff_name} • ${timesheet.staff_id}`
                : ""}
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="rounded-lg px-3 py-2 text-sm text-slate-500 hover:bg-slate-100"
          >
            Close
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 py-6">
          {loading ? (
            <p className="text-sm text-slate-500">Loading timesheet...</p>
          ) : error ? (
            <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
              {error}
            </div>
          ) : timesheet ? (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div className="rounded-xl bg-slate-50 p-4">
                  <p className="text-xs text-slate-500">Period</p>
                  <p className="mt-1 text-sm font-semibold text-slate-900">
                    {formatDisplayDate(timesheet.period_start)} -{" "}
                    {formatDisplayDate(timesheet.period_end)}
                  </p>
                </div>

                <div className="rounded-xl bg-emerald-50 p-4 ring-1 ring-emerald-100">
                  <p className="text-xs text-emerald-600">
                    Total Hours Worked
                  </p>
                  <p className="mt-1 text-sm font-semibold text-emerald-900">
                    {formatHoursWorked(timesheet.total_minutes)}
                  </p>
                </div>
              </div>

              <div className="rounded-xl border border-slate-200 bg-white">
                <div className="border-b border-slate-200 px-4 py-3">
                  <h4 className="text-sm font-semibold text-slate-900">
                    Daily Breakdown (completed shifts only)
                  </h4>
                </div>

                {!timesheet.rows || timesheet.rows.length === 0 ? (
                  <div className="px-4 py-4 text-sm text-slate-500">
                    No attendance records for this period.
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[560px]">
                      <thead className="bg-slate-50">
                        <tr className="text-left">
                          <th className="px-4 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                            Date
                          </th>
                          <th className="px-4 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                            Clock In
                          </th>
                          <th className="px-4 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                            Clock Out
                          </th>
                          <th className="px-4 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                            Hours
                          </th>
                          <th className="px-4 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                            Status
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {timesheet.rows.map((row) => (
                          <tr key={row.id}>
                            <td className="px-4 py-3 text-sm text-slate-700">
                              {formatDisplayDate(row.clock_in)}
                            </td>
                            <td className="px-4 py-3 text-sm text-slate-700">
                              {formatDisplayTime(row.clock_in)}
                            </td>
                            <td className="px-4 py-3 text-sm text-slate-700">
                              {row.clock_out
                                ? formatDisplayTime(row.clock_out)
                                : "—"}
                            </td>
                            <td className="px-4 py-3 text-sm text-slate-700">
                              {row.hours_worked_minutes != null
                                ? formatHoursWorked(row.hours_worked_minutes)
                                : "—"}
                            </td>
                            <td className="px-4 py-3 text-sm text-slate-700">
                              {row.status}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                <div className="border-t border-slate-200 bg-slate-50 px-4 py-3">
                  <p className="text-sm font-semibold text-slate-900">
                    Total Hours Worked:{" "}
                    <span className="text-emerald-700">
                      {formatHoursWorked(timesheet.total_minutes)}
                    </span>
                  </p>
                </div>
              </div>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default function TimesheetsPage() {
  const token = localStorage.getItem("staff_portal_token");
  const defaultWeek = useMemo(() => getCurrentWeekRange(), []);

  const [weekStart, setWeekStart] = useState(defaultWeek.weekStart);
  const [weekEnd, setWeekEnd] = useState(defaultWeek.weekEnd);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [hoursReport, setHoursReport] = useState([]);

  const [showViewModal, setShowViewModal] = useState(false);
  const [viewLoading, setViewLoading] = useState(false);
  const [viewError, setViewError] = useState("");
  const [selectedTimesheet, setSelectedTimesheet] = useState(null);

  const periodLabel = useMemo(() => {
    if (!weekStart || !weekEnd) return "—";
    return `${formatDisplayDate(weekStart)} - ${formatDisplayDate(weekEnd)}`;
  }, [weekStart, weekEnd]);

  const handleLoadHours = useCallback(async () => {
    if (!weekStart || !weekEnd) {
      setError("Please select both start and end dates.");
      return;
    }

    if (new Date(weekEnd) < new Date(weekStart)) {
      setError("End date cannot be earlier than start date.");
      return;
    }

    setLoading(true);
    setError("");

    const res = await apiRequest({
      script: "admin-weekly-wages",
      token,
      data: {
        week_start: weekStart,
        week_end: weekEnd,
      },
    });

    setLoading(false);

    if (!res?.success) {
      setError(res?.error || "Failed to load staff hours.");
      return;
    }

    setHoursReport(res.wages || []);
  }, [token, weekStart, weekEnd]);

  useEffect(() => {
    handleLoadHours();
  }, [handleLoadHours]);

  const handleDownloadTimesheet = (staffUserId) => {
    if (!staffUserId || !token) return;

    const form = document.createElement("form");
    form.method = "POST";
    form.action = PROXY_URL;
    form.target = "_blank";
    form.style.display = "none";

    const fields = {
      script: "admin-download-timesheet",
      api_token: token,
      staff_user_id: String(staffUserId),
      week_start: weekStart,
      week_end: weekEnd,
    };

    Object.entries(fields).forEach(([name, value]) => {
      const input = document.createElement("input");
      input.type = "hidden";
      input.name = name;
      input.value = value;
      form.appendChild(input);
    });

    document.body.appendChild(form);
    form.submit();
    document.body.removeChild(form);
  };

  const handleViewTimesheet = async (staffUserId) => {
    setShowViewModal(true);
    setViewLoading(true);
    setViewError("");
    setSelectedTimesheet(null);

    const res = await apiRequest({
      script: "admin-get-timesheet",
      token,
      data: {
        staff_user_id: staffUserId,
        week_start: weekStart,
        week_end: weekEnd,
      },
    });

    setViewLoading(false);

    if (!res?.success) {
      setViewError(res?.error || "Failed to load timesheet.");
      return;
    }

    setSelectedTimesheet(res.timesheet);
  };

  const handleCloseViewModal = () => {
    setShowViewModal(false);
    setSelectedTimesheet(null);
    setViewError("");
  };

  return (
    <AdminLayout title="Timesheets">
      <FullPageSpinner show={loading} />

      <div className="space-y-6">
        {error ? (
          <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
            {error}
          </div>
        ) : null}

        <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 shadow-sm">
          <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
            <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">
                  Start Date
                </label>
                <input
                  type="date"
                  value={weekStart}
                  onChange={(e) => setWeekStart(e.target.value)}
                  className="w-full rounded-xl border border-slate-300 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">
                  End Date
                </label>
                <input
                  type="date"
                  value={weekEnd}
                  onChange={(e) => setWeekEnd(e.target.value)}
                  className="w-full rounded-xl border border-slate-300 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="col-span-2 md:col-span-1 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                <p className="text-xs text-slate-500">Selected Period</p>
                <p className="mt-1 text-sm font-semibold text-slate-900">
                  {periodLabel}
                </p>
              </div>
            </div>

            <div>
              <button
                type="button"
                onClick={handleLoadHours}
                className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-slate-900 px-5 py-3 text-sm font-medium text-white transition hover:bg-slate-800 xl:w-auto"
              >
                <CalendarDays size={18} />
                Load Hours
              </button>
            </div>
          </div>
        </div>

        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="flex items-center justify-between border-b border-slate-200 bg-gradient-to-r from-slate-50 to-emerald-50 px-6 py-5">
            <div>
              <h3 className="text-lg font-semibold text-slate-900">
                Timesheets
              </h3>
              <p className="mt-1 text-sm text-slate-500">
                Total hours worked for the selected period
              </p>
            </div>

            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-emerald-100 ring-1 ring-emerald-200">
              <Timer size={20} className="text-emerald-700" />
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full min-w-[820px]">
              <thead className="border-b border-slate-200 bg-slate-50">
                <tr className="text-left">
                  <th className="px-6 py-4 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Staff
                  </th>
                  <th className="px-6 py-4 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Staff ID
                  </th>
                  <th className="px-6 py-4 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Hours Worked
                  </th>
                  <th className="px-6 py-4 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Action
                  </th>
                </tr>
              </thead>

              <tbody>
                {hoursReport.length === 0 ? (
                  <tr>
                    <td
                      colSpan="4"
                      className="px-6 py-10 text-center text-sm text-slate-500"
                    >
                      No attendance records for the selected period.
                    </td>
                  </tr>
                ) : (
                  hoursReport.map((item, index) => (
                    <tr
                      key={item.staff_user_id}
                      className={`border-b border-slate-100 last:border-b-0 ${
                        index % 2 === 0 ? "bg-white" : "bg-slate-50/60"
                      }`}
                    >
                      <td className="px-6 py-4 text-sm font-semibold text-slate-900">
                        {item.staff_name}
                      </td>

                      <td className="px-6 py-4 text-sm text-slate-700">
                        {item.staff_id}
                      </td>

                      <td className="px-6 py-4 text-sm text-slate-700">
                        {formatHoursWorked(item.total_minutes)}
                      </td>

                      <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-2">
                          <button
                            type="button"
                            onClick={() =>
                              handleViewTimesheet(item.staff_user_id)
                            }
                            className="inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                          >
                            <Eye size={16} />
                            View
                          </button>

                          <button
                            type="button"
                            onClick={() =>
                              handleDownloadTimesheet(item.staff_user_id)
                            }
                            className="inline-flex items-center gap-2 rounded-xl border border-blue-300 bg-white px-4 py-2 text-sm font-medium text-blue-700 hover:bg-blue-50"
                          >
                            <Download size={16} />
                            Download
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <TimesheetViewModal
        isOpen={showViewModal}
        onClose={handleCloseViewModal}
        loading={viewLoading}
        error={viewError}
        timesheet={selectedTimesheet}
      />
    </AdminLayout>
  );
}
